import React, { useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, Container, ProgressBar, Row, Spinner,
} from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { FaCheckCircle, FaCircle, FaExternalLinkAlt } from 'react-icons/fa';
import { getReviewSetDetail } from '@/api/reviewSetApi';
import type { ReviewSetChildReviewDto, ReviewSetInstanceDetailDto } from '@/types/reviews';
import './ReviewDetail.scss';

const formatDate = (d?: string) => (d ? new Date(d).toLocaleDateString() : '—');

const reviewStatusVariant = (status: string, isComplete: boolean) => {
  if (isComplete) return 'success';
  switch (status?.toLowerCase()) {
    case 'in progress': return 'primary';
    case 'assigned': return 'warning';
    case 'cancelled': return 'danger';
    default: return 'secondary';
  }
};

const ReviewSetDetail: React.FC = () => {
  const { setInstanceGuid } = useParams<{ setInstanceGuid: string }>();
  const [detail, setDetail] = useState<ReviewSetInstanceDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!setInstanceGuid) return;
    (async () => {
      try {
        const data = await getReviewSetDetail(setInstanceGuid);
        setDetail(data);
      } catch (err: any) {
        setError(err?.message ?? 'Failed to load review set.');
      } finally {
        setLoading(false);
      }
    })();
  }, [setInstanceGuid]);

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (error || !detail) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error ?? 'Review set not found.'}</Alert>
        <Link to="/review-sets">← My Review Sets</Link>
      </Container>
    );
  }

  const primaryOwner = detail.owners.find((o) => o.isPrimary);
  const otherOwners = detail.owners.filter((o) => !o.isPrimary);

  return (
    <Container className="mt-4 pb-5 review-detail-page">
      <div className="mb-3">
        <Link to="/review-sets" className="text-secondary small">← My Review Sets</Link>
      </div>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h4 className="mb-1">{detail.setInstanceName}</h4>
          <div className="text-secondary small">
            <Badge bg="secondary" className="me-2">{detail.setTemplateCategory}</Badge>
            {detail.setTemplateName}
          </div>
        </div>
        <div className="text-end">
          {detail.isComplete
            ? <Badge bg="success" className="fs-6">Complete</Badge>
            : <Badge bg={detail.progressPercent > 0 ? 'primary' : 'warning'}
                     text={detail.progressPercent > 0 ? undefined : 'dark'}
                     className="fs-6">
                {detail.progressPercent > 0 ? 'In Progress' : 'Not Started'}
              </Badge>
          }
        </div>
      </div>

      {/* Meta */}
      <Row className="g-2 mb-4">
        {detail.assignedDate && (
          <Col xs="auto" className="text-secondary small">
            Assigned: <strong>{formatDate(detail.assignedDate)}</strong>
          </Col>
        )}
        {detail.dueDate && (
          <Col xs="auto" className="text-secondary small">
            Due: <strong>{formatDate(detail.dueDate)}</strong>
          </Col>
        )}
        {detail.completedDate && (
          <Col xs="auto" className="text-secondary small">
            Completed: <strong>{formatDate(detail.completedDate)}</strong>
          </Col>
        )}
      </Row>

      {detail.setInstanceDescription && (
        <Alert variant="info" className="py-2 mb-3">{detail.setInstanceDescription}</Alert>
      )}

      {/* Owners */}
      <Card className="mb-4">
        <Card.Header><strong>Owners</strong></Card.Header>
        <Card.Body className="py-2">
          {primaryOwner && (
            <div className="d-flex align-items-center gap-2 mb-1">
              <Badge bg="info" text="dark">Primary</Badge>
              <span>{primaryOwner.ownerEmail}</span>
            </div>
          )}
          {otherOwners.map((o) => (
            <div key={o.ownerEmail} className="text-secondary small ms-1">{o.ownerEmail}</div>
          ))}
        </Card.Body>
      </Card>

      {/* Overall Progress */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between">
          <strong>Overall Progress</strong>
          <span className="text-secondary small">
            {detail.completedReviews} of {detail.totalReviews} reviews complete
          </span>
        </Card.Header>
        <Card.Body>
          <ProgressBar
            now={detail.progressPercent}
            label={`${detail.progressPercent}%`}
            variant={detail.isComplete ? 'success' : 'primary'}
            style={{ height: 22 }}
          />
        </Card.Body>
      </Card>

      {/* Child Reviews */}
      <h5 className="mb-3">Reviews</h5>
      {detail.reviews.length === 0 && (
        <Alert variant="secondary">No individual reviews found in this set.</Alert>
      )}
      {detail.reviews.map((review) => (
        <ReviewCard key={review.reviewGuid} review={review} />
      ))}
    </Container>
  );
};

const ReviewCard: React.FC<{ review: ReviewSetChildReviewDto }> = ({ review }) => {
  const variant = reviewStatusVariant(review.reviewStatus, review.isComplete);

  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              {review.isComplete
                ? <FaCheckCircle className="text-success" />
                : <FaCircle className="text-secondary" style={{ opacity: 0.3 }} />
              }
              <strong>{review.templateName}</strong>
              <Badge bg="secondary" className="ms-1">{review.templateCategory}</Badge>
            </div>
            <div className="text-secondary small ms-4">
              Assigned to: <strong>{review.assignedTo}</strong>
              {review.dueDate && <span className="ms-2">· Due {formatDate(review.dueDate)}</span>}
              {review.completedDate && <span className="ms-2">· Completed {formatDate(review.completedDate)}</span>}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Badge bg={variant}>
              {review.isComplete ? 'Complete' : review.reviewStatus}
            </Badge>
            <Link to={`/reviews/${review.reviewGuid}`}>
              <Button variant="outline-secondary" size="sm">
                <FaExternalLinkAlt className="me-1" />
                Open
              </Button>
            </Link>
          </div>
        </div>

        {review.totalTasks > 0 && (
          <ProgressBar
            now={review.progressPercent}
            label={`${review.completedTasks}/${review.totalTasks} tasks`}
            variant={review.isComplete ? 'success' : 'primary'}
            style={{ height: 16 }}
            className="ms-4"
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default ReviewSetDetail;
